<template>
  <v-menu bottom right transition="slide-x-transition">
    <template v-slot:activator="{on, attrs}">
      <div v-bind="attrs" v-on="on">
        <v-icon :data-cy="`menu-${item.id}`" class="icon-mapit notranslatable">)</v-icon>
      </div>
    </template>

    <v-list class="list-menu-actions" dense>
      <v-list-item @click="viewOfferSummary(item)">
        <v-list-item-title>
          {{ $t('offers.actions.view') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item :disabled="!canEditOffer(item.status)" @click="editOffer(item.id)">
        <v-list-item-title>
          {{ $t('offers.actions.edit') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item :disabled="!canCreateRegistration(item.status)" @click="createRegistration(item.id)">
        <v-list-item-title>
          {{ $t('offers.actions.register') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item @click="printPdf(item.id)">
        <v-list-item-title>
          {{ $t('offers.actions.print') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item @click="downloadPdf(item)">
        <v-list-item-title>
          {{ $t('offers.actions.downloadPDF') }}
        </v-list-item-title>
      </v-list-item>

      <v-divider />

      <v-list-item :disabled="!canChangeToAccepted(item.status)" @click="$emit('changeStatus', item, Status.Accepted)">
        <v-list-item-title>
          {{ $t('offers.actions.accept') }}
        </v-list-item-title>
      </v-list-item>

      <v-list-item :disabled="!canChangeToRefused(item.status)" @click="$emit('changeStatus', item, Status.Refused)">
        <v-list-item-title>
          {{ $t('offers.actions.refuse') }}
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
</template>
<script lang="ts">
import {defineComponent, PropType} from '@vue/composition-api'
import printJS from 'print-js'

import {downloadFile} from '@/helpers/downloadFile'
import {useGtm} from '@/plugins/gtm'
import {Offer, Status} from '@/types/offers'
import {RegistrationTypes} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'
import {isRegistrationAllowed} from '@/utils/registration-helper'

export default defineComponent({
  name: 'OfferTableActions',
  props: {
    item: {
      type: Object as PropType<Offer>,
      required: true,
    },
  },
  components: {},
  setup(_, {root}) {
    const gtm = useGtm()
    const {getGroup: group} = mapGetters(root.$store.getters)

    function viewOfferSummary(offer: Offer) {
      if (!offer?.id) {
        console.error('This offer does not exists', offer)
        return
      }

      gtm?.trackEvent({
        category: 'Offers',
        action: 'ViewOfferSummary',
        label: 'FromOffersList',
      })

      root.$router.push({name: 'ResumeOfferMotorbike', params: {id: offer.id}})
    }

    function canEditOffer(status: Status) {
      return [Status.Accepted, Status.Pending].includes(status)
    }

    async function editOffer(offerId: string) {
      gtm?.trackEvent({
        category: 'Offers',
        action: 'EditOffer',
        label: 'FromOffersList',
      })

      root.$store.commit('RESET_OFFER')
      await root.$store.dispatch('setGroupToOffer', group.value)
      await root.$store.dispatch('loadOfferById', offerId)

      root.$router.push({name: 'EditOfferMotorbike', params: {id: offerId}})
    }

    function canCreateRegistration(status: Status) {
      return isRegistrationAllowed(status)
    }

    function createRegistration(offerId: string) {
      gtm?.trackEvent({
        category: 'Offers',
        action: 'CreateRegistrationFromOffer',
        label: 'FromOffersList',
      })
      root.$router.push({
        name: 'HondaRegistration',
        query: {forcedType: RegistrationTypes.OFFER_REGISTRATION, offerId},
      })
    }

    function printPdf(offerId: string) {
      gtm?.trackEvent({
        category: 'Offers',
        action: 'PrintOffer',
        label: 'FromOffersList',
      })

      root.$store
        .dispatch('loadOfferSummaryById', {id: offerId, dealerId: group.value.dealerId})
        .then(({pdf, code}) => {
          printJS({
            printable: pdf,
            type: 'pdf',
            documentTitle: `${group.value.code}-${code?.split('-')[1] ?? offerId}.pdf`,
          })
        })
    }

    function downloadPdf(offer: Offer) {
      gtm?.trackEvent({
        category: 'Offers',
        action: 'DownloadOffer',
        label: 'FromOffersList',
      })

      root.$store
        .dispatch('loadOfferSummaryById', {id: offer.id, dealerId: group.value.dealerId})
        .then(({pdf, code}) => {
          downloadFile(pdf, `${group.value.code}-${code?.split('-')[1] ?? offer.id}.pdf`)
        })
    }

    function canChangeToAccepted(status: Status) {
      return status === Status.Pending
    }

    function canChangeToRefused(status: Status) {
      return status === Status.Pending || status === Status.Accepted
    }

    return {
      Status,
      viewOfferSummary,
      canEditOffer,
      editOffer,
      canCreateRegistration,
      createRegistration,
      printPdf,
      downloadPdf,
      canChangeToAccepted,
      canChangeToRefused,
    }
  },
})
</script>
